import { useMemo } from "react"
import { Box, ToggleButtonGroup, useTheme } from "@mui/material"
import { CalendarStarIcon } from "components/icons"

import {
  CURRENT_EVENT_FILTER_VALUE,
  UPCOMING_EVENT_FILTER_VALUE,
} from "constant"

import { getIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"
import { useEvent } from "hooks/event/useEvent"

import { PageCard, PageCardContent } from "components/card"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { VenueSelector } from "components/VenueSelector"
import { EmptyData } from "components/EmptyData"

import {
  FeatureCarouselLayout,
  FeatureGridLayout,
  TLayoutType,
} from "components/domains/features"

import { CategoryFilterChip } from "components/CategoryFilterBar"
import { SidebarMenu } from "components/navigation/SidebarMenu"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { IContentWithAsideProps } from "layouts/kiosk-h/ContentWithAside"
import clsx from "clsx"

const DEFAULT_EVENT_LAYOUT_TYPE: TLayoutType[] = ["carousel", "grid"]
const DEFAULT_EVENT_DISPLAY_MODE = ["all", "grouped"]

interface IEventsKioskHPageProps {
  Layout?: React.FC<IContentWithAsideProps>
  AppNavigation?: React.ComponentType
}

export const EventsKioskHPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
}: IEventsKioskHPageProps) => {
  const { t } = useTranslation()

  const {
    isMultiVenueProject,
    venueFilter,
    venues,
    locationVenueId,
    groupEvents,
    activeGroupEvent,
    events,
    handleChange,
    changeVenueFilter,
  } = useEvent()

  const theme = useTheme()

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.events) || <CalendarStarIcon />,
    [theme]
  )
  // TODO: config `eventLayoutType`, `eventDisplayMode` from app config
  const {
    eventCarouselOption,
    eventLayoutType = "carousel",
    eventDisplayMode = "grouped",
  } = theme.components[`EventPage`]?.defaultProps || {}

  const { styleOverrides } = theme.components?.[`EventPage`] || {}
  const {
    pageCard,
    pageCardContent,
    carouselContainer,
    carousel,
    emptyDataWrapper,
    venueSelector,
    eventGroupSelectorButton,
    gridContainer,
  } = styleOverrides

  const validateEventLayoutType = useMemo(
    () =>
      DEFAULT_EVENT_LAYOUT_TYPE.includes(eventLayoutType)
        ? eventLayoutType
        : "carousel",
    [eventLayoutType]
  )

  const validateEventDisplayMode = useMemo(
    () =>
      DEFAULT_EVENT_DISPLAY_MODE.includes(eventDisplayMode)
        ? eventDisplayMode
        : "all",
    [eventDisplayMode]
  )

  const displayEvents = useMemo(() => {
    return validateEventDisplayMode === "all"
      ? events
      : groupEvents[activeGroupEvent]
  }, [validateEventDisplayMode, events, groupEvents, activeGroupEvent])

  usePageSideBarLayout({
    sidebar: {
      open: false,
    },
  })

  return (
    <Layout
      aside={
        <PageCard>
          <SidebarMenu AppNavigation={AppNavigation} />
        </PageCard>
      }
    >
      <PageCard sx={pageCard}>
        <PageCardContent
          sx={pageCardContent}
          className={clsx(
            `EventLayoutType-${validateEventLayoutType}`,
            `EventDisplayMode-${validateEventDisplayMode}`
          )}
        >
          {displayEvents && displayEvents.length > 0 ? (
            validateEventLayoutType === "carousel" ? (
              <FeatureCarouselLayout
                sx={carouselContainer}
                features={displayEvents}
                carouselOption={eventCarouselOption}
                carouselSx={carousel}
                linkPrefix="/events"
              />
            ) : (
              <FeatureGridLayout
                sx={gridContainer}
                features={displayEvents}
                layoutType={validateEventLayoutType}
                linkPrefix="/events"
              />
            )
          ) : (
            <Box sx={emptyDataWrapper}>
              <EmptyData
                icon={emptyIcon}
                primary={t("No Current Events")}
                secondary={t("Check Back Soon")}
              />
            </Box>
          )}

          {isMultiVenueProject && (
            <Box sx={venueSelector}>
              <VenueSelector
                selectedVenue={venueFilter}
                venues={venues}
                onClickVenue={changeVenueFilter}
                currentVenue={locationVenueId}
              />
            </Box>
          )}

          {validateEventDisplayMode === "grouped" && (
            <Box sx={eventGroupSelectorButton}>
              <ToggleButtonGroup
                value={activeGroupEvent}
                onChange={handleChange}
                exclusive
              >
                <CategoryFilterChip value={CURRENT_EVENT_FILTER_VALUE}>
                  {t("Current Events")}
                </CategoryFilterChip>
                <CategoryFilterChip value={UPCOMING_EVENT_FILTER_VALUE}>
                  {t("Upcoming Events")}
                </CategoryFilterChip>
              </ToggleButtonGroup>
            </Box>
          )}
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
